import type Luminary from '@lumen5/luminary';
import type { Scene } from '@lumen5/luminary';
import type { GetRootState, RootState } from 'js/src/libs/RootState';

import type { LintFinding } from '../videoLinter';

import { getVideoLanguage } from 'js/src/data/app/app-selectors';
import { getLanguageReadingSpeed } from 'js/src/data/translations';
import { _calculateTextDurationInMs, defaultLanguageReadingSpeed } from 'js/src/libs/timing-utils';
import {
    getEditableSlideTextArray,
    getRevealedTextAssets,
    isSceneAnOverlay,
    isVoiceDrivenSceneGroup,
} from 'js/src/luminary/luminary-selectors';
import { getVoicedrivenSceneDuration } from 'js/src/luminary/voicedriven-scenes';

/**
 * Fraction of the comfortable reading time an overlay must clear before we stop warning.
 *
 * Deliberately lenient. Unlike a text-on-media scene, a voice-driven overlay is timed by the
 * narration underneath it, so it is normal (and correct) for one to be tighter than a leisurely
 * read — the viewer also hears the words. We only want to catch text that flashes by too fast to
 * be read at all, not every overlay that is a little brisk, so an overlay only has to clear 60% of
 * the comfortable reading time.
 */
const READABLE_FRACTION = 0.6;

/**
 * Below this, an overlay reads as a flash regardless of how little text it holds: even two words
 * cannot be recognised and understood in under a second.
 */
const ABSOLUTE_FLOOR_MS = 1000;

/**
 * Reading time here is measured at the unmodified base pace. See the call site for why this check
 * deliberately ignores the video's pace multiplier.
 */
const PACE_INDEPENDENT_MULTIPLIER = 100;

const seconds = (ms: number): string => (ms / 1000).toFixed(1);

/** Words-per-minute for the video's language, which every reading-time judgement here is made at. */
export function videoReadingSpeed(state: RootState): number {
    const videoLanguage = state.app ? getVideoLanguage(state) : null;
    return getLanguageReadingSpeed(state, videoLanguage) ?? defaultLanguageReadingSpeed;
}

/**
 * How long a text revealed `introMs` into its container stays on screen: it goes when the container
 * does, and on a multi-subscene scene the first subscene ends before the scene, and on a scene with
 * a transition the logical (transition-excluded) duration is the shorter one.
 */
export function revealedTextVisibleMs(
    scene: Scene,
    containerDurationMs: number,
    introMs: number,
): number {
    return Math.min(containerDurationMs, getVoicedrivenSceneDuration(scene)) - introMs;
}

/** The comfortable read time for `text` and the bar a voice-driven overlay holds it to. */
function readingTimes(text: string, readingSpeed: number) {
    const comfortableDuration = _calculateTextDurationInMs(
        text,
        PACE_INDEPENDENT_MULTIPLIER,
        readingSpeed,
    );
    return {
        comfortableDuration,
        minimumDuration: Math.max(comfortableDuration * READABLE_FRACTION, ABSOLUTE_FLOOR_MS),
        floorBinds: comfortableDuration * READABLE_FRACTION < ABSOLUTE_FLOOR_MS,
    };
}

/** Whether `text` on screen for `visibleMs` is too brief to be read, by the bar this check holds. */
export function isTooBriefToRead(text: string, visibleMs: number, readingSpeed: number): boolean {
    return visibleMs < readingTimes(text, readingSpeed).minimumDuration;
}

/**
 * Warning: an overlay scene in a voice-driven scene group is on screen too briefly for a viewer to
 * read its text.
 *
 * Voice-driven overlays are timed by the narration rather than by reading speed, so the text-on-
 * media model in `scene_reading_time` does not apply — its `SCENE_DURATIONS.MIN` floor of 3.4s
 * would flag most legitimately short overlays. This check instead compares the overlay's duration
 * against the raw time its text needs to be read, with a lenient margin, so it only fires when the
 * text genuinely cannot be read in the time it is up.
 *
 * The same bar is held per text asset revealed partway through a scene (`set_text` with
 * `start_words`): an overlay long enough for its text still fails a line that only lands as the
 * overlay is about to end.
 *
 * Warning only — the author may have deliberately used a fast cut.
 */
export function checkVoicedrivenSceneReadingTime(
    luminary: Luminary,
    getState: GetRootState,
): LintFinding[] {
    const findings: LintFinding[] = [];
    const state = getState();
    const readingSpeed = videoReadingSpeed(state);

    luminary.sceneGroups.forEach((sceneGroup) => {
        if (!isVoiceDrivenSceneGroup(sceneGroup)) {
            return;
        }

        sceneGroup.scenes.forEach((scene) => {
            findings.push(...revealedTextFindings(scene, readingSpeed));

            if (!isSceneAnOverlay(state, scene)) {
                return;
            }

            const sceneText = getEditableSlideTextArray(scene).join(' ').trim();
            if (sceneText.length === 0) {
                return;
            }

            // Deliberately pace-independent. The pace multiplier scales *recommended* text-on-media
            // durations, and never reaches a voice-driven scene: those are timed by the narration
            // (`recalculateSceneDuration` bails out on voice-driven groups). Folding pace in here
            // would swing the bar +/-20% with the Fast/Normal/Slow control while the overlay's real
            // on-screen time never moved — warning on sound overlays at Slow and missing real
            // flashes at Fast. How long text takes to read is a property of the text, not the pace.
            const { comfortableDuration, minimumDuration, floorBinds } = readingTimes(
                sceneText,
                readingSpeed,
            );
            const duration = getVoicedrivenSceneDuration(scene);
            if (duration >= minimumDuration) {
                return;
            }

            // Explain whichever constraint actually fired. On a short overlay the absolute floor
            // binds well above the text's own reading time, and citing that reading time there
            // would read as a contradiction ("only 0.9s on screen but needs about 0.1s to read")
            // and advise shortening text that is already as short as it can get.
            const actualSeconds = (duration / 1000).toFixed(1);
            const message = floorBinds
                ? `Overlay is only ${actualSeconds}s on screen, under the ${(ABSOLUTE_FLOOR_MS / 1000).toFixed(1)}s minimum any overlay needs to register at all, so it will flash by unread. Extend the overlay over more of the narration.`
                : `Overlay is only ${actualSeconds}s on screen but its text needs about ${(comfortableDuration / 1000).toFixed(1)}s to read, so it will flash by unread. Either shorten the text or extend the overlay over more of the narration.`;

            findings.push({
                check: 'voicedriven_scene_reading_time',
                severity: 'warning',
                sceneId: scene.id,
                message: `${message} This may be intentional — confirm the fast cut is deliberate.`,
            });
        });
    });

    return findings;
}

/**
 * One finding per text asset that lands too late in its scene to be read before the scene ends,
 * judged by the same lenient bar as the overlay itself. Applies to every scene in the group, base
 * or overlay: a late reveal over the speaker is just as unreadable as one over b-roll.
 */
function revealedTextFindings(scene: Scene, readingSpeed: number): LintFinding[] {
    return getRevealedTextAssets(scene).flatMap(({ asset, introMs, containerDurationMs }) => {
        const visibleMs = revealedTextVisibleMs(scene, containerDurationMs, introMs);
        const { comfortableDuration, minimumDuration, floorBinds } = readingTimes(
            asset.text,
            readingSpeed,
        );
        if (visibleMs >= minimumDuration) {
            return [];
        }
        const message = floorBinds
            ? `"${asset.name}" appears ${seconds(introMs)}s into the scene and is on screen for only ${seconds(visibleMs)}s, under the ${seconds(ABSOLUTE_FLOOR_MS)}s minimum any text needs to register, so it will flash by unread. Reveal it on earlier words, or extend the scene over more of the narration.`
            : `"${asset.name}" appears ${seconds(introMs)}s into the scene and is on screen for only ${seconds(visibleMs)}s, but its text needs about ${seconds(comfortableDuration)}s to read, so it will flash by unread. Reveal it on earlier words, or extend the scene over more of the narration.`;
        return [
            {
                check: 'voicedriven_scene_reading_time' as const,
                severity: 'warning' as const,
                sceneId: scene.id,
                message: `${message} This may be intentional — confirm the fast cut is deliberate.`,
            },
        ];
    });
}
